import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const ElectronicsSecurity = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{translations['electronics.title']} | Risk Consulting | Fepsec</title>
        <meta name="description" content={translations['electronics.meta.description']} />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-800 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-aos="fade-up">{translations['electronics.title']}</h1>
            <p className="text-xl text-indigo-100 mb-8" data-aos="fade-up" data-aos-delay="100">
              {translations['electronics.hero.subtitle']}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['electronics.intro.title']}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['electronics.intro.p1']}
            </p>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
              {translations['electronics.intro.p2']}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="100">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-blue-100 mb-4">{translations['electronics.expertise.title']}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.expertise.item1']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.expertise.item2']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.expertise.item3']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.expertise.item4']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.expertise.item5']}</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="200">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-blue-100 mb-4">{translations['electronics.benefits.title']}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.benefits.item1']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.benefits.item2']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.benefits.item3']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.benefits.item4']}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-600 mr-2">✓</span>
                  <span>{translations['electronics.benefits.item5']}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['electronics.services.title']}</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-purple-700 dark:text-purple-400 mb-3">{translations['electronics.services.cctv.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['electronics.services.cctv.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-purple-700 dark:text-purple-400 mb-3">{translations['electronics.services.access.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['electronics.services.access.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-purple-700 dark:text-purple-400 mb-3">{translations['electronics.services.intrusion.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['electronics.services.intrusion.description']}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-purple-700 dark:text-purple-400 mb-3">{translations['electronics.services.integration.title']}</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {translations['electronics.services.integration.description']}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-blue-100 mb-6">{translations['electronics.cta.title']}</h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8">
              {translations['electronics.cta.description']}
            </p>
            <a 
              href="/contact-us" 
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
            >
              {translations['electronics.cta.button']}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectronicsSecurity;
